import React, { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Shield, Zap, Search, Brain, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { StickyScrollingCards } from './ui/sticky-scrolling-cards';

gsap.registerPlugin(ScrollTrigger);

const features = [
  {
    num: '01',
    icon: Shield,
    title: 'SECURITY ASSESSMENT',
    description: 'Detects potential security risks and hidden vulnerabilities that could compromise your network safety.',
    link: '/network-scanner'
  },
  {
    num: '02',
    icon: Zap,
    title: 'PERFORMANCE ANALYSIS',
    description: 'Evaluates network speed, latency, and bandwidth to ensure optimal performance and reliability.',
    link: '/network-scanner'
  },
  {
    num: '03',
    icon: Search,
    title: 'REAL-TIME SCANNING',
    description: 'Continuously monitors network connections and provides instant feedback on potential issues.',
    link: '/network-scanner'
  },
  {
    num: '04',
    icon: Brain,
    title: 'AI-POWERED INSIGHTS',
    description: 'Our advanced AI system analyzes results and provides actionable recommendations for improvement.',
    link: '/network-scanner'
  },
];

const FeatureCard = ({ feature }: { feature: typeof features[0] }) => {
  const Icon = feature.icon;
  return (
    <div className="flex flex-col gap-6 justify-center w-full bg-black rounded-3xl pb-4">
      {/* Large Illustration Box (Transparent/Floating in Dark Theme) */}
      <div className="w-full aspect-[4/3] sm:aspect-[16/9] lg:aspect-auto lg:h-[280px] xl:h-[320px] rounded-3xl flex items-center justify-center relative group">
        
        {/* Center Icon (Light line art style) */}
        <div className="relative z-10 flex items-center justify-center transition-all duration-500 group-hover:scale-105">
          <Icon className="w-32 h-32 sm:w-40 sm:h-40 text-[#3b82f6] transition-transform duration-500 group-hover:-rotate-3" strokeWidth={1} />
        </div>

      </div>

      {/* Text Content */}
      <div className="flex flex-col gap-3 px-2">
        <div className="flex items-center gap-2 text-sm font-bold tracking-widest uppercase">
          <span className="text-[#3b82f6]">{feature.num}</span>
          <span className="w-1 h-1 rounded-full bg-[#3b82f6]" />
          <span className="text-slate-500">CYBER DEFENSE</span>
        </div>
        
        <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight uppercase">
          {feature.title}
        </h3>
        
        <p className="text-slate-400 text-base sm:text-lg leading-relaxed max-w-2xl">
          {feature.description}
        </p>

        <Link 
          to={feature.link}
          className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-white hover:text-[#3b82f6] transition-colors group w-fit px-5 py-2.5 rounded-full border border-white/20 hover:border-[#3b82f6] bg-transparent"
        >
          Know More
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
};

const NetworkScannerSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useLayoutEffect(() => {
    let ctx = gsap.matchMedia();

    ctx.add("(min-width: 1024px)", () => {
      // DESKTOP: Pinned Scroll Animation
      const cards = cardsRef.current;
      if (!cards.length || !sectionRef.current || !leftRef.current) return;

      gsap.set(leftRef.current, { opacity: 0, y: 30 });
      
      gsap.to(leftRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 70%",
        }
      });

      gsap.set(cards.slice(1), { autoAlpha: 0, y: 50 });
      gsap.set(cards[0], { autoAlpha: 1, y: 0 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "center center",
          end: `+=${cards.length * 100}%`,
          pin: true,
          scrub: 1,
          anticipatePin: 1,
        }
      });

      cards.forEach((card, index) => {
        if (index === 0) return;
        
        const prevCard = cards[index - 1];
        
        tl.to(prevCard, { autoAlpha: 0, y: -50, duration: 1, ease: "power2.inOut" }, `step${index}`)
          .to(card, { autoAlpha: 1, y: 0, duration: 1, ease: "power2.inOut" }, `step${index}`);
      });
    });

    ctx.add("(max-width: 1023px)", () => {
       // MOBILE/TABLET: Standard scrolling (No Pin)
       const cards = cardsRef.current;
       
       gsap.set(leftRef.current, { opacity: 1, y: 0, clearProps: "all" });
       gsap.set(cards, { clearProps: "all" });

       gsap.fromTo(leftRef.current, 
         { opacity: 0, y: 30 },
         { 
           opacity: 1, 
           y: 0, 
           duration: 0.8,
           scrollTrigger: {
             trigger: leftRef.current,
             start: "top 80%",
           }
         }
       );

       cards.forEach((card) => {
         gsap.fromTo(card, 
           { autoAlpha: 0, y: 40 },
           { 
             autoAlpha: 1, 
             y: 0, 
             duration: 0.6,
             scrollTrigger: {
               trigger: card,
               start: "top 85%",
             }
           }
         );
       });
    });

    return () => ctx.revert();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="relative py-24 text-slate-100 w-full flex items-center min-h-[80vh] bg-black"
    >
      {/* Background Noise Texture */}
      <div 
        className="absolute inset-0 z-0 pointer-events-none mix-blend-overlay opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }}
      />
      
      {/* 3D Decorative Glowing Orbs - Switched to Blue/Indigo for Scanner */}
      <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-[#3b82f6]/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-40 right-10 w-72 h-72 rounded-full bg-[#6366f1]/10 blur-[120px] pointer-events-none" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        <div className="flex flex-col lg:flex-row-reverse gap-12 lg:gap-20 items-center">
          
          {/* LEFT SIDE: Static Info */}
          <div className="w-full lg:w-5/12 xl:w-1/3" ref={leftRef}>
            <div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 tracking-tight leading-[1.1]">
                AI Network<br />
                <span 
                  className="text-transparent bg-clip-text" 
                  style={{ backgroundImage: 'linear-gradient(130deg, rgb(59, 130, 246) 0%, rgb(96, 165, 250) 55%, rgb(59, 130, 246) 100%)' }}
                >
                  Integrity Scanner
                </span>
              </h2>
              
              <div className="text-sm md:text-base text-slate-400 font-light mt-6 leading-relaxed space-y-4 pr-4">
                <p>
                  Our 100% free AI Network Integrity Scanner lets you instantly check for security threats, performance bottlenecks, and connectivity issues with a single click.
                </p>
                <p>
                  Powered by cutting-edge AI, it intelligently analyzes your network to detect vulnerabilities and provides personalized, actionable security recommendations.
                </p>
              </div>

              <div className="mt-8">
                <Link
                  to="/network-scanner"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#3b82f6] text-white font-bold text-base rounded-full hover:bg-[#60a5fa] transition-all duration-300 shadow-[0_0_30px_rgba(59,130,246,0.25)] hover:-translate-y-0.5 group"
                >
                  <span>Launch Network Scanner</span>
                  <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE DESKTOP: Animated Cards Container */}
          <div className="hidden lg:block w-full lg:w-7/12 xl:w-2/3 relative h-[500px] xl:h-[550px]" ref={rightColumnRef}>
            {features.map((feature, index) => (
              <div 
                key={index}
                ref={el => cardsRef.current[index] = el}
                className="absolute inset-0 flex flex-col gap-6 justify-center"
              >
                <FeatureCard feature={feature} />
              </div>
            ))}
          </div>

          {/* RIGHT SIDE MOBILE: Sticky Scrolling Cards */}
          <div className="block lg:hidden w-full mt-16">
            <StickyScrollingCards items={features.map(feature => <FeatureCard feature={feature} />)} />
          </div>

        </div>
      </div>
    </section>
  );
};

export default NetworkScannerSection;
